import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { ProjectPermissionKeySchema } from '../permissions/catalogue.js';
import { LanguageSchema } from '../common/language.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { ResponsibilityLevelSchema } from '../responsibilities/levels.js';
// Request schemas for the roles feature (project-owned entity per CONVENTIONS
// §Entity categories — each project defines its own roles). Roles management is
// admin-only (sysadmin / project_admin); there is no `roles:manage` permission
// key. All routes are project-scoped (`/api/projects/:projectId/roles/*`); roles
// never move between projects, so there is no platform surface.
//
// `name` .max(32) matches roles.name varchar(32); CI-unique per project
// (uq_roles_name_ci). description is a TEXT column → application cap .max(500).
const roleFields = {
    name: z.string().trim().min(1).max(32),
    description: z.string().trim().max(500).nullable().optional(),
    inactivity_timeout_min: z.number().int().positive().nullable().optional(),
    // Full-replace sets. PRESENT (even []) ⇒ replace; ABSENT (undefined) ⇒ leave
    // unchanged. `permissions` = the capability keys the role grants — bare keys
    // (ADR-025: no values; the typed catalogue in src/permissions is the enforced
    // form). Validated against the project-grantable enum: an unknown OR platform-
    // tier key is rejected here (400); keys whose `requires` are unmet are
    // normalized — dropped — server-side on write (never auto-added). `managed_roles`
    // = role IDs a holder of THIS role may assign to users (delegation;
    // role_managed_roles).
    permissions: z.array(ProjectPermissionKeySchema).max(200).optional(),
    managed_roles: z.array(z.number().int().positive()).max(500).optional(),
    // `responsibility_levels` = the inline level-set narrowing for `users:manage`'s
    // accountability action (ADR-026 §E): which ResponsibilityLevels a holder may ASSIGN to
    // users. Full-replace like the sets above. Stored now; the narrowing's ENFORCEMENT rides
    // the assignment surface (APP-185).
    responsibility_levels: z.array(ResponsibilityLevelSchema).max(7).optional(),
};
// ── POST /api/projects/:projectId/roles ─────────────────────────────────────
export const CreateRoleRequestSchema = z.object({ ...roleFields }).strict();
// ── PATCH /api/projects/:projectId/roles/:roleId ────────────────────────────
// Partial: every field optional; at least one required. permissions /
// managed_roles follow the present-vs-absent replace rule above.
export const UpdateRoleRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(32).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    inactivity_timeout_min: z.number().int().positive().nullable().optional(),
    permissions: z.array(ProjectPermissionKeySchema).max(200).optional(),
    managed_roles: z.array(z.number().int().positive()).max(500).optional(),
    responsibility_levels: z.array(ResponsibilityLevelSchema).max(7).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' });
// ── GET /api/projects/:projectId/roles/export — current-view export ─────────
// Mirrors the list's URL view-state so the file IS what the user sees (ADR-045).
// `status`/`usage` arrive comma-separated (the binary list filters); `q` is the
// free-text search; `lang` drives header/sheet localization. Read visibility
// (managed_roles narrowing) is always applied server-side — export is
// self-bounding. Gated on the read key (`users:manage`), like the list.
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(64)).max(50).optional());
export const ExportRolesQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    status: csvFilter,
    usage: csvFilter,
    q: z.string().trim().max(100).optional(),
})
    .strict();
